// src/modules/brands/models/brand.model.ts
export type { UserInfo } from "@modules/shared/models/user.model";

export interface MediaDto {
  key: string;
  url: string;
}

export type BrandMember = {
  user_id: string;
  branches: string[];
};

export interface BrandDto {
  id: string;
  name: string;
  owner_id: string;
  members: BrandMember[];
  logo: MediaDto | null;
  banner: MediaDto | null;
  media: MediaDto[];
  description: string;
  show_social: boolean;
  contract_id?: string;
  contact_phone?: string;
  contact_email?: string;
  scan_record?: string[];
  created_at: string;
  updated_at: string;
}

export interface BrandHighlightDto {
  b2b_id: string;
  order_index: number;
}

export interface ShopDto {
  id: string;
  name: string;
  location: string;
  b2b_id: string;
  image: string | null;
  created_at: string;
  updated_at: string;
}

export type CreateBrandInput = {
  contract_id: string;
  name: string;
  contact_phone: string;
  contact_email: string;
  description: string;
  owner_id?: string;
  show_social?: boolean;
  scan_record?: string[];
};

export type UpdateBrandInput = Partial<
  Pick<BrandDto, "name" | "description" | "show_social"> & {
    contact_phone?: string;
    contact_email?: string;
    contract_id?: string;
    owner_id?: string;
    scan_record?: string[];
  }
>;

export const mapMediaDto = (value: unknown): MediaDto | null => {
  if (!value) return null;
  if (typeof value === "string") {
    const url = String(value);
    return url ? { key: "", url } : null;
  }
  if (typeof value !== "object") return null;
  const media = value as Record<string, unknown>;
  return {
    key: String(media.key ?? ""),
    url: String(media.url ?? ""),
  };
};

export const mapBrandDto = (value: unknown): BrandDto => {
  const data = (value ?? {}) as Record<string, unknown>;
  const id = String(data.id ?? data._id ?? "");
  return {
    id,
    name: String(data.name ?? ""),
    owner_id: String(data.owner_id ?? data.ownerId ?? data.user_id ?? ""),
    members: Array.isArray(data.members)
      ? data.members
          .map((item): BrandMember | null => {
            if (!item || typeof item !== "object") return null;
            const m = item as Record<string, unknown>;
            const user_id = String(m.user_id ?? m.userId ?? "");
            if (!user_id) return null;

            const branchesRaw = m.branches ?? m.branchIds ?? m.branch_ids;
            if (Array.isArray(branchesRaw)) {
              const branches = branchesRaw
                .map((b) => String(b))
                .filter(Boolean);
              return { user_id, branches };
            }

            // ponytail: legacy { user_id, branch_id } rows → fold into branches[]
            const branch_id = String(m.branch_id ?? m.branchId ?? "");
            return branch_id ? { user_id, branches: [branch_id] } : null;
          })
          .filter((m): m is BrandMember => m !== null)
      : [],
    logo: mapMediaDto(data.logo),
    banner: mapMediaDto(data.banner),
    media: Array.isArray(data.media)
      ? data.media.map(mapMediaDto).filter((m): m is MediaDto => m !== null)
      : [],
    description: String(data.description ?? ""),
    show_social: Boolean(data.show_social ?? data.showSocial),
    contract_id: String(data.contract_id ?? data.contractId ?? ""),
    contact_phone: String(data.contact_phone ?? data.contactPhone ?? ""),
    contact_email: String(data.contact_email ?? data.contactEmail ?? ""),
    scan_record: Array.isArray(data.scan_record)
      ? data.scan_record
          .map((item: any) => {
            if (typeof item === "string") return item;
            if (item && typeof item === "object") {
              return item.url || "";
            }
            return "";
          })
          .filter(Boolean)
      : [],
    created_at: String(data.created_at ?? data.createdAt ?? ""),
    updated_at: String(data.updated_at ?? data.updatedAt ?? ""),
  };
};

export const mapBrandHighlightDto = (value: unknown): BrandHighlightDto => {
  const data = (value ?? {}) as Record<string, unknown>;
  let orderIndex = 0;
  if (typeof data.order_index === "number") orderIndex = data.order_index;
  else if (typeof data.orderIndex === "number") orderIndex = data.orderIndex;
  else orderIndex = Number(data.order_index ?? data.orderIndex ?? 0);
  return {
    b2b_id: String(data.b2b_id ?? data.b2bId ?? ""),
    order_index: orderIndex,
  };
};

export const mapShopDto = (value: unknown): ShopDto => {
  const data = (value ?? {}) as Record<string, unknown>;
  const id = String(data.id ?? data._id ?? "");
  return {
    id,
    name: String(data.name ?? ""),
    location: String(data.location ?? ""),
    b2b_id: String(data.b2b_id ?? data.b2bId ?? ""),
    image: String(data.image ?? data.image_url ?? "") || null,
    created_at: String(data.created_at ?? data.createdAt ?? ""),
    updated_at: String(data.updated_at ?? data.updatedAt ?? ""),
  };
};
